import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnInit, ViewChild} from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective, FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms'
import {TranslateService} from "@ngx-translate/core";
import {distinctUntilChanged, of, switchMap} from "rxjs";
import {BaseApiEndpointService} from "../../../../../services/api/services/BaseApiEndpoint.service";
import {ListItem} from "ng-multiselect-dropdown/multiselect.model";
import {CoreModule} from "../../../../core.module";
import {SingleResponse} from "@generated-backend-types";


@Component({
    selector: 'app-read-only-with-service[service]',
    templateUrl: './read-only-with-service.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useClass: ReadOnlyWithServiceComponent,
            multi: true
        }],
  imports: [CoreModule, FormsModule],
  standalone: true
})
export class ReadOnlyWithServiceComponent<Model extends BaseResource, Data = Model> implements ControlValueAccessor, OnInit {

  @Input() public label!: string
  @Input() public name!: string
  @Input() public customClasses = '';
  @Input() public errorMessages!: Record<string, string>


  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl!: FormControl<unknown>

  @Input()
  public formControlName!: string;

  @Input() public service!: BaseApiEndpointService<Model, Data>

  @Input() public overrideMultiSelectText?: keyof Model;

  @Input() public withTranslationKey?: string;

  public readonlyItem: ListItem | null = null;

  public get control(): FormControl<Model[] | string | null | undefined | string[]> {
    if (this.formControl) {
      return <FormControl<Model[] | string | null | undefined | string[]>>this.formControl
    }
    const controlCont = this.controlContainer
    if (this.formControlName && controlCont?.control !== null) {
      return controlCont?.control.get(this.formControlName) as FormControl<Model[] | string | null | undefined | string[]>;
    }
    return new FormControl<Model[] | string | null | undefined | string[]>(null)
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(obj: number | null): void {
    this.formControlDirective?.valueAccessor?.writeValue(obj);
  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben
  }

  public ngOnInit() {
    if(this.control?.value) {
      // @ts-expect-error unreasonable expression call error
      this.fetchResourceViaService(this.control?.value).subscribe({
        next: (value: SingleResponse<Model> | null) => {
          this.setInternalControlValueViaServiceResponse(value);
        }
      });
    }

    this.control.valueChanges.pipe(
      distinctUntilChanged(),
      switchMap(value => {
          return this.fetchResourceViaService(value);
        }
      )
    ).subscribe(
      value => {
        this.setInternalControlValueViaServiceResponse(value);
      }
    )
  }

  private setInternalControlValueViaServiceResponse(value: SingleResponse<Model> | null) {
    if (value && value.data) {
      this.readonlyItem = this.service.modelToMultiSelectItem(value.data);
      if(this.overrideMultiSelectText && this.readonlyItem) this.readonlyItem.text = value.data[this.overrideMultiSelectText] as unknown as string;
      if(this.withTranslationKey && this.readonlyItem.text) this.readonlyItem.text = this.translate.instant(this.withTranslationKey + '.' + this.readonlyItem.text);
      this.cdr.markForCheck();
    } else {
      this.readonlyItem = {isDisabled: true, text: '', id: ''}
    }
  }

  private fetchResourceViaService(value: string | Model[] | string[] | null | undefined) {
    if (typeof value === 'string') {
      return this.service.get(value);
    } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return this.service.get(value[0]);
    } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0]?.id) {
      return this.service.get(value[0].id);
    }
    return of(null);
  }

  constructor(private readonly cdr: ChangeDetectorRef, private readonly injector: Injector, private translate: TranslateService) {}
}
